import styled from "styled-components";

export const DataViewContainer = styled.div`
  padding: 24px;
  font-family: "Readex Pro", sans-serif;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
`;

export const DataViewHeader = styled.div`
  margin-bottom: 24px;
`;

export const DataViewTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

export const DataViewDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

export const InfoBanner = styled.div`
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "ℹ️";
    font-size: 16px;
  }
`;

export const Table = styled.table`
  width: 100%;
  min-width: 800px; /* Ensures horizontal scroll for smaller viewports */
  border-collapse: collapse;
`;

export const TableHeader = styled.thead`
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

export const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: auto;

  /* Sticky header for better UX when scrolling */
  & ${Table} ${TableHeader} {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f9fafb;
  }
`;

export const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr<{ isExpanded?: boolean }>`
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9fafb;
  }
`;

export const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #374151;
  vertical-align: middle;
`;

const syncStatusColors: Record<
  "synced" | "legacy_only" | "gg_only",
  { bg: string; color: string; border: string }
> = {
  synced: { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
  legacy_only: { bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
  gg_only: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
};

export const SyncStatusBadge = styled.span<{
  status: "synced" | "legacy_only" | "gg_only";
  stale?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(p) =>
    p.stale ? "#fef2f2" : syncStatusColors[p.status]?.bg ?? "#f3f4f6"};
  color: ${(p) =>
    p.stale ? "#dc2626" : syncStatusColors[p.status]?.color ?? "#6b7280"};
  border: 1px solid
    ${(p) =>
      p.stale ? "#fecaca" : syncStatusColors[p.status]?.border ?? "#e5e7eb"};
`;

export const StatusBadge = styled.span<{ deprecated?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => (props.deprecated ? "#fef2f2" : "#ecfdf5")};
  color: ${(props) => (props.deprecated ? "#dc2626" : "#059669")};
  border: 1px solid ${(props) => (props.deprecated ? "#fecaca" : "#a7f3d0")};
`;

export const ExpandButton = styled.button<{ isExpanded?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: transform 0.2s, color 0.2s;
  transform: ${(props) =>
    props.isExpanded ? "rotate(90deg)" : "rotate(0deg)"};

  &:hover {
    color: #55b8b3;
  }

  &:focus {
    outline: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 16px;
`;

export const LoadingMessage = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 16px;
`;

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  color: #d1d5db;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

export const EmptyStateDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  text-align: center;
  max-width: 400px;
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button<{
  active?: boolean;
  filterKey?: "all" | "synced" | "gg_only" | "legacy_only" | "not_in_gg";
}>`
  padding: 5px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${(p) => {
      if (!p.active) return "#e5e7eb";
      switch (p.filterKey) {
        case "synced":
          return "#a7f3d0";
        case "legacy_only":
          return "#fde68a";
        case "gg_only":
          return "#bfdbfe";
        case "not_in_gg":
          return "#fecaca";
        default:
          return "#d1d5db";
      }
    }};
  background-color: ${(p) => {
    if (!p.active) return "#ffffff";
    switch (p.filterKey) {
      case "synced":
        return "#ecfdf5";
      case "legacy_only":
        return "#fffbeb";
      case "gg_only":
        return "#eff6ff";
      case "not_in_gg":
        return "#fef2f2";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(p) => {
    if (!p.active) return "#6b7280";
    switch (p.filterKey) {
      case "synced":
        return "#059669";
      case "legacy_only":
        return "#d97706";
      case "gg_only":
        return "#2563eb";
      case "not_in_gg":
        return "#dc2626";
      default:
        return "#374151";
    }
  }};
  transition: all 0.15s;

  &:hover {
    border-color: #9ca3af;
    color: #374151;
  }

  &:focus {
    outline: none;
  }
`;

export const FilterCount = styled.span`
  font-size: 12px;
  color: #9ca3af;
  margin-left: 4px;
`;
